import { useMemo } from "react";
import { Builder } from "@builder.io/react";
import { captureException } from "@sentry/nextjs";
import { ConfigProvider } from "antd";

import { analytics } from "@flare/analytics";
import { useTranslation } from "@flare/i18n";
import { signOut, useSession } from "@flare/next-auth/react";

import { useIsDesktop } from "@/hooks/use-is-desktop";
import {
  PERSONAL_INFO_URL,
  PRE_CLIENT_PATH,
  SIGNIN_PAGE_URL,
} from "@/modules/pre-client/pre-client.consts";
import { BiEventsNames } from "@/services/analytics/bi-event-names";
import { useFeatureFlag } from "@/services/experiments";
import { featureFlagNames } from "@/services/experiments/constants";
import { deprecatedColors } from "@/styles/deprecated-colors";

import { DesktopNavbar, MobileNavbar } from "./components";
import type { NavbarItem } from "./NavbarV2.types";
import { parseMenuItems } from "./NavbarV2.utils";

const attorneysPageConfig = {
  label: "For attorneys",
  children: [],
  href: "co-counsels/home",
  openInNewTab: false,
  key: "for_attorneys",
};

const personalInfoPageConfig = {
  label: "Personal Information",
  children: [],
  href: PERSONAL_INFO_URL,
  openInNewTab: false,
  key: "personal_info",
};

const baseItems: NavbarItem[] = [
  {
    label: "Our services",
    key: "our_services",
    children: [
      {
        label: "Family law",
        key: "family_law",
        children: [
          {
            label: "Learn about family law →",
            pathname: "/family",
            key: "learn_about_family_law",
          },
          { label: "Divorce", pathname: "/family/divorce", key: "divorce" },
          {
            label: "Child custody & visitation",
            pathname: "/family/childcustodyandvisitation",
            key: "child_custody_and_visitation",
          },
          {
            label: "Alimony & spousal support",
            pathname: "/family/alimonyandspousalsupport",
            key: "alimony_and_spousal_support",
          },
          {
            label: "Mediation",
            pathname: "/family/mediation",
            key: "mediation",
          },
          {
            label: "Restraining order",
            pathname: "/family/restrainingorder",
            key: "restraining_order",
          },
          {
            label: "Adoption",
            pathname: "/family/adoption",
            key: "adoption",
          },
        ],
      },
      {
        label: "Immigration law",
        key: "immigration_law",
        children: [
          {
            label: "Learn about immigration law →",
            pathname: "/immigration",
            key: "learn_about_immigration_law",
          },
          {
            label: "Green card",
            pathname: "/immigration/green-card",
            key: "green_card",
          },
          {
            label: "Work visa",
            pathname: "/immigration/workvisas",
            key: "work_visa",
          },
        ],
      },
    ],
  },
  {
    label: "How we're different",
    key: "how_we_are_different",
    children: [
      { label: "About us", pathname: "/about-us", key: "about_us" },
      {
        label: "Your legal team",
        pathname: "/our-attorneys",
        key: "your_legal_team",
      },
      {
        label: "How we price",
        pathname: "/pricing",
        key: "how_we_price",
      },
      { label: "Reviews", pathname: "/reviews", key: "reviews" },
      {
        label: "Our pro bono work",
        pathname: "/pro-bono",
        key: "our_pro_bono_work",
      },
    ],
  },
  {
    label: "Resources & help",
    key: "resources_and_help",
    children: [
      {
        label: "Marble blog",
        href: "https://blog.marble.co",
        openInNewTab: false,
        key: "marble_blog",
      },
      {
        label: "Help center",
        href: "https://helpcenter.marble.co/help-center",
        openInNewTab: true,
        key: "help_center",
      },
      {
        label: "Attorney answers",
        key: "attorney_answers",
        openInNewTab: false,
        children: [
          { label: "Family", pathname: "/family/answers", key: "family" },
          {
            label: "Immigration",
            pathname: "/immigration/answers",
            key: "immigration",
          },
        ],
      },
      {
        label: "Cost calculator",
        key: "cost_calculator",
        children: [
          {
            label: "Family law case",
            pathname: "/family/cost-calculator",
            key: "cost_calculator_family_law",
          },
        ],
      },
    ],
  },
];

export function NavbarV2() {
  const session = useSession();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const preClientAbTest = useFeatureFlag(
    featureFlagNames.FUNNEL_TEST_PRE_CLIENT_PORTAL,
  );

  const isSignedIn =
    preClientAbTest.isReady &&
    preClientAbTest.isOn &&
    session?.status !== "loading" &&
    session?.data?.provider !== "anonymous";

  const firstName = session.data?.user?.name?.first;
  const userInitials = firstName ? firstName[0].toUpperCase() : "";
  const isMobile = !isDesktop;

  const parsedItems = useMemo(() => {
    const items = [...baseItems];
    if (!isSignedIn) {
      items.push(attorneysPageConfig);
    } else if (isMobile) {
      items.push(personalInfoPageConfig);
    }

    return parseMenuItems(items);
  }, [isMobile, isSignedIn]);

  function signinClickHandler() {
    analytics.track(BiEventsNames.WebPageClick, {
      location: "navbar",
      element_name: "navbar",
      click_text: t("SIGN_IN"),
      click_url:
        preClientAbTest.isReady && preClientAbTest.isOn
          ? SIGNIN_PAGE_URL
          : process.env.NEXT_PUBLIC_CUSTOMERS_APP_URL || "",
      click_type: "click",
    });
  }

  function funnelCtaClickHandler() {
    analytics.track(BiEventsNames.WebPageCtaClick, {
      element_name: "navbar",
      click_text: t("GET_STARTED"),
      click_url: "/v2/funnel",
      click_type: "click",
    });
  }

  function marbleHomeClickHandler() {
    analytics.track(BiEventsNames.WebPageClick, {
      location: "navbar",
      element_name: "navbar",
      click_text: t("GO_TO_YOUR_MARBLE_HOME"),
      click_url: PRE_CLIENT_PATH,
      click_type: "click",
    });
  }

  function personalInfoClickHandler() {
    analytics.track(BiEventsNames.WebPageClick, {
      location: "navbar",
      element_name: "navbar",
      click_text: t("PERSONAL_INFO"),
      click_url: PERSONAL_INFO_URL,
      click_type: "click",
    });
  }

  async function signoutClickHandler() {
    analytics.track(BiEventsNames.WebPageClick, {
      location: "navbar",
      element_name: "navbar",
      click_text: t("SIGN_OUT"),
      click_type: "click",
    });

    try {
      await signOut({ redirect: false });
    } catch (error) {
      captureException(error, {
        extra: {
          action: "signout",
        },
      });
    }
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "Roobert",
          colorPrimary: deprecatedColors.primary,
          colorText: deprecatedColors.b100,
          colorBgTextHover: deprecatedColors.g20,
          fontSize: 18,
          lineHeight: 0.64,
          controlHeightLG: 72,
          colorIcon: deprecatedColors.primary,
        },
      }}
    >
      <div className="md:hidden">
        <MobileNavbar
          items={parsedItems as NavbarItem[]}
          isSignedIn={isSignedIn}
          userInitials={userInitials}
          onMarbleHomeClick={marbleHomeClickHandler}
          onSignoutClick={signoutClickHandler}
          onSigninClick={signinClickHandler}
          onFunnelCtaClick={funnelCtaClickHandler}
          onPersonalInfoClick={personalInfoClickHandler}
          preClientAbTest={preClientAbTest}
        />
      </div>
      <div className="hidden md:block">
        <DesktopNavbar
          items={parsedItems as NavbarItem[]}
          isSignedIn={isSignedIn}
          userInitials={userInitials}
          onMarbleHomeClick={marbleHomeClickHandler}
          onPersonalInfoClick={personalInfoClickHandler}
          onSignoutClick={signoutClickHandler}
          onSigninClick={signinClickHandler}
          onFunnelCtaClick={funnelCtaClickHandler}
          preClientAbTest={preClientAbTest}
        />
      </div>
    </ConfigProvider>
  );
}

Builder.registerComponent(NavbarV2, {
  name: "Navbar",
});
